import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { Release } from '../../models/release';
import { ReleaseContextService } from '../../services/release-context/release-context.service';
import { activeReleasesByStart } from '../../services/release/release-sort';
import { ToastService } from '../toast/toast.service';

/** How many shipped/held releases the dropdown lists before "See all releases". */
const RECENT_LIMIT = 5;

/**
 * The release chip in the app header, and the switcher behind it (PBI 55102 AC3–AC5,
 * Bug 56801). Shows which release you are looking at and opens a dropdown to change
 * it without leaving the page you are on.
 *
 * The dropdown is a CDK menu, so Escape, outside clicks and returning focus to the
 * chip come from the CDK rather than hand-rolled listeners (AC5).
 */
@Component({
  selector: 'app-release-switcher',
  standalone: true,
  imports: [CdkMenuTrigger, CdkMenu, CdkMenuItem, RouterLink],
  templateUrl: './release-switcher.component.html',
  styleUrl: './release-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseSwitcherComponent {
  private context = inject(ReleaseContextService);
  private router = inject(Router);
  private toast = inject(ToastService);

  readonly current = this.context.current;
  readonly isLoading = signal(false);

  /** Active releases, listed first under their own heading, earliest start first.
      There can be several running at once, so all of them are switchable here. */
  readonly activeReleases = computed(() => activeReleasesByStart(this.context.releases()));

  /** Shipped and held releases, newest first. Archived ones are left to the full
      Releases page, which the link at the bottom of the dropdown goes to. */
  readonly recentReleases = computed(() =>
    this.context
      .releases()
      .filter((r) => r.status === 'Shipped' || r.status === 'Held')
      .sort((a, b) => +new Date(b.createdOnUtc) - +new Date(a.createdOnUtc))
      .slice(0, RECENT_LIMIT),
  );

  /** True once there is something to show, so the menu can render an empty state. */
  readonly hasOptions = computed(
    () => this.activeReleases().length > 0 || this.recentReleases().length > 0,
  );

  isCurrent(r: Release): boolean {
    return r.releaseId === this.current()?.releaseId;
  }

  /** Fetched on first open, so the header costs nothing on pages nobody switches from. */
  onOpened(): void {
    if (this.context.releases().length) return;

    this.isLoading.set(true);
    this.context
      .loadReleases()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {},
        error: () => this.toast.error('Could not load releases. Please try again.'),
      });
  }

  select(r: Release): void {
    if (this.isCurrent(r)) return;

    // Update the chip before the data lands so the header reflects the choice
    // immediately (AC3); the page that owns the context refreshes it on load.
    this.context.setCurrent(r);

    if (this.isInsights()) {
      // Insights reads the release off the query string, so switching reloads the
      // current tab in place instead of navigating away from the page (AC4).
      void this.router.navigate(['/insights'], {
        queryParams: { releaseId: r.releaseId },
        queryParamsHandling: 'merge',
      });
      return;
    }

    void this.router.navigate(['/releases', r.releaseId]);
  }

  private isInsights(): boolean {
    return this.router.url.split('?')[0].startsWith('/insights');
  }
}
